import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { badgeRecipe } from './recipes/badge';
import { buttonRecipe } from './recipes/button';
import { carouselDotsRecipe } from './recipes/carousel-dots';
import { checkboxSlotRecipe } from './recipes/checkbox';
import { dialogSlotRecipe } from './recipes/dialog';
import { inputRecipe, textareaRecipe } from './recipes/input';
import { optionTileRecipe } from './recipes/option-tile';
import { popoverSlotRecipe } from './recipes/popover';
import { segmentedControlSlotRecipe } from './recipes/segmented-control';
import { sheetSlotRecipe } from './recipes/sheet';
import { toastSlotRecipe } from './recipes/toast';
import { toggleRecipe } from './recipes/toggle';
import { tooltipSlotRecipe } from './recipes/tooltip';

/** Match / chart player accents — earthy Runeterra tones tuned for dark UI. */
export const MATCH_PLAYER_COLORS = {
  A: {
    hex: '#C3753F',
    bg: 'rgba(195, 117, 63, 0.14)',
    palette: 'orange',
  },
  B: {
    hex: '#A5AF6C',
    bg: 'rgba(165, 175, 108, 0.14)',
    palette: 'green',
  },
  C: {
    hex: '#014F30',
    bg: 'rgba(1, 79, 48, 0.22)',
    palette: 'teal',
  },
  D: {
    hex: '#23505A',
    bg: 'rgba(35, 80, 90, 0.22)',
    palette: 'cyan',
  },
  /** Antique gold — accent-adjacent, for charts & extra participants. */
  E: {
    hex: '#C4A055',
    bg: 'rgba(196, 160, 85, 0.16)',
    palette: 'yellow',
  },
  /** Soft amethyst — ties to brand violet without overpowering slices. */
  F: {
    hex: '#9178BF',
    bg: 'rgba(145, 120, 191, 0.16)',
    palette: 'purple',
  },
  /** Dusty mauve — warm rose for high-tier / overflow series. */
  G: {
    hex: '#9E7A8C',
    bg: 'rgba(158, 122, 140, 0.16)',
    palette: 'pink',
  },
  /** Muted lavender grey — catch-all / unknown buckets. */
  H: {
    hex: '#756B8A',
    bg: 'rgba(117, 107, 138, 0.16)',
    palette: 'gray',
  },
} as const;

export type MatchPlayerColorSlot = keyof typeof MATCH_PLAYER_COLORS;

/** Ordered slots for multi-series charts (pie, momentum, etc.). */
export const MATCH_PLAYER_COLOR_SLOTS = Object.keys(MATCH_PLAYER_COLORS) as MatchPlayerColorSlot[];

export function matchPlayerColorHex(index: number): string {
  const slot = MATCH_PLAYER_COLOR_SLOTS[index % MATCH_PLAYER_COLOR_SLOTS.length];
  return MATCH_PLAYER_COLORS[slot].hex;
}

// Night palette: dark purple/indigo with gold accents. Light mode overrides
// these CSS variables in colorSchemes.ts when the system prefers light.
const config = defineConfig({
  theme: {
    tokens: {
      colors: {
        riftbound: {
          bg: { value: '#0F0B1A' },
          bgElevated: { value: '#1A1626' },
          surface: { value: '#221C32' },
          border: { value: '#3A2F50' },
          text: { value: '#F2EDFF' },
          textMuted: { value: '#9C8FBF' },
          accent: { value: '#D2B45A' },
          /** Slightly brighter gold for primary CTA hover (solid default uses `accent`). */
          accentHover: { value: '#E5CA70' },
          accentMuted: { value: '#8C7637' },
          violet: { value: '#7C5CFF' },
          danger: { value: '#F25C5C' },
          success: { value: '#5CE0B0' },
          matchPlayer: {
            A: { value: MATCH_PLAYER_COLORS.A.hex },
            B: { value: MATCH_PLAYER_COLORS.B.hex },
            C: { value: MATCH_PLAYER_COLORS.C.hex },
            D: { value: MATCH_PLAYER_COLORS.D.hex },
            E: { value: MATCH_PLAYER_COLORS.E.hex },
            F: { value: MATCH_PLAYER_COLORS.F.hex },
            G: { value: MATCH_PLAYER_COLORS.G.hex },
            H: { value: MATCH_PLAYER_COLORS.H.hex },
          },
          /** Charts & deck stats — aliases into matchPlayer for a unified viz palette. */
          graph: {
            energyCurve: { value: '{colors.riftbound.matchPlayer.E}' },
            powerCurve: { value: '{colors.riftbound.matchPlayer.G}' },
            cardTypeUnit: { value: '{colors.riftbound.matchPlayer.A}' },
            cardTypeGear: { value: '{colors.riftbound.matchPlayer.D}' },
            cardTypeSpell: { value: '{colors.riftbound.matchPlayer.F}' },
            cardTypeChampion: { value: '{colors.riftbound.matchPlayer.E}' },
            cardTypeBattlefield: { value: '{colors.riftbound.matchPlayer.C}' },
            cardTypeOther: { value: '{colors.riftbound.matchPlayer.H}' },
          },
          keyword: {
            // Teal group
            reaction:     { value: '#1EA289' },
            action:       { value: '#1EA289' },
            accelerate:   { value: '#1EA289' },
            hidden:       { value: '#1EA289' },
            quickdraw:    { value: '#1EA289' },
            showoff:      { value: '#1EA289' },
            deploy:       { value: '#1EA289' },
            repeat:       { value: '#1EA289' },
            // Pink group
            assault:      { value: '#D4367A' },
            assault2:     { value: '#D4367A' },
            shield:       { value: '#D4367A' },
            shield2:      { value: '#D4367A' },
            tank:         { value: '#D4367A' },
            // Olive group
            deathknell:   { value: '#8DB820' },
            deflect:      { value: '#8DB820' },
            temporary:    { value: '#8DB820' },
            // Gray group
            add:          { value: '#7A7A7A' },
            equip:        { value: '#7A7A7A' },
            predict:      { value: '#6B6F70' },
            predict2:     { value: '#6B6F70' },
            '1':            { value: '#FFFFFF' },
            '2':            { value: '#FFFFFF' },
            // CSS-only fallbacks
            ambush:       { value: '#7B3A2A' },
            rally:        { value: '#9A7B2A' },
            overwhelm:    { value: '#C0442A' },
            elusive:      { value: '#2A6B9A' },
            fearsome:     { value: '#5A2A7B' },
            challenger:   { value: '#B06020' },
            barrier:      { value: '#2A7BB0' },
            ephemeral:    { value: '#4A4A5A' },
            scout:        { value: '#3A7B3A' },
            lifesteal:    { value: '#8B1A3A' },
            tough:        { value: '#5A5040' },
            quickattack:  { value: '#9A8A20' },
            spellshield:  { value: '#2A4A9A' },
            regeneration: { value: '#2A8B4A' },
            default:      { value: '#3A2F50' },
          },
        },
      },
      fonts: {
        heading: { value: '"Beaufort for LOL", "Georgia", serif' },
        body: { value: '"Spiegel", "Arial", sans-serif' },
        mono: { value: '"Spiegel", "Courier New", monospace' },
      },
      /** Ordinary strokes. Every palette uses this; thicker rings stay on their own values. */
      borderWidths: {
        hairline: { value: '0.5px' },
      },
      borders: {
        hairline: { value: '{borderWidths.hairline} solid' },
      },
    },
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
      badge: badgeRecipe,
      optionTile: optionTileRecipe,
      toggle: toggleRecipe,
      carouselDots: carouselDotsRecipe,
    },
    slotRecipes: {
      riftboundCheckbox: checkboxSlotRecipe,
      riftboundPopover: popoverSlotRecipe,
      riftboundDialog: dialogSlotRecipe,
      riftboundTooltip: tooltipSlotRecipe,
      riftboundToast: toastSlotRecipe,
      segmentedControl: segmentedControlSlotRecipe,
      sheet: sheetSlotRecipe,
    },
    semanticTokens: {
      colors: {
        /**
         * Chakra overlays (Popover, Menu, Dialog, Select, …) resolve
         * `colors.bg.panel` via CSS vars like `--popover-bg`. `bg` must stay a
         * nested token group — a flat `bg: { value }` replaces that tree and
         * breaks panel backgrounds (transparent menus).
         */
        bg: {
          DEFAULT: { value: '{colors.riftbound.bg}' },
          subtle: { value: '{colors.riftbound.bgElevated}' },
          muted: { value: '{colors.riftbound.bgElevated}' },
          emphasized: { value: '{colors.riftbound.surface}' },
          inverted: { value: '{colors.riftbound.text}' },
          panel: { value: '{colors.riftbound.surface}' },
          error: { value: '{colors.riftbound.danger}' },
          warning: { value: '{colors.riftbound.accentMuted}' },
          success: { value: '{colors.riftbound.success}' },
          info: { value: '{colors.riftbound.violet}' },
        },
        bgElevated: { value: '{colors.riftbound.bgElevated}' },
        surface: { value: '{colors.riftbound.surface}' },
        border: { value: '{colors.riftbound.border}' },
        text: { value: '{colors.riftbound.text}' },
        textMuted: { value: '{colors.riftbound.textMuted}' },
        accent: { value: '{colors.riftbound.accent}' },
        accentHover: { value: '{colors.riftbound.accentHover}' },
        accentMuted: { value: '{colors.riftbound.accentMuted}' },
        selected: {
          bg: { value: '{colors.riftbound.accent}' },
          fg: { value: '{colors.riftbound.bg}' },
        },
        /**
         * Color palette for Chakra `colorPalette` (buttons, badges, …).
         * Primary CTAs use `solid` + `contrast` — logo gold family, not preset gray
         * (undefined palette defaults to gray → white fill in dark mode).
         */
        brandGold: {
          contrast: { value: '{colors.riftbound.bg}' },
          fg: { value: '{colors.riftbound.text}' },
          subtle: { value: '{colors.riftbound.surface}' },
          muted: { value: '{colors.riftbound.border}' },
          emphasized: { value: '{colors.riftbound.accent}' },
          solid: { value: '{colors.riftbound.accent}' },
          focusRing: { value: '{colors.riftbound.accent}' },
          border: { value: '{colors.riftbound.accent}' },
        },
      },
    },
  },
  globalCss: {
    'html, body': {
      backgroundColor: '{colors.riftbound.bg}',
      color: '{colors.riftbound.text}',
      minHeight: '100vh',
      WebkitTapHighlightColor: 'transparent',
    },
    a: {
      color: '{colors.riftbound.accent}',
    },
    'button, [role="button"], a, summary, label[for], input[type="checkbox"], input[type="radio"], input[type="submit"], input[type="button"]':
      {
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      },
    // Active only while play / match / event routes set this attribute on touch
    // devices. :where() keeps score-drag `pan-y` and dice `none` in charge of
    // those surfaces. user-select / touch-callout stop the iOS Copy menu on
    // labels such as round results; fields and join codes stay selectable.
    'html[data-lock-viewport-scale], html[data-lock-viewport-scale] body': {
      touchAction: 'pan-x pan-y',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
    },
    'html[data-lock-viewport-scale] :where(body *)': {
      touchAction: 'manipulation',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
    },
    'html[data-lock-viewport-scale] :where(input, textarea, code, [contenteditable="true"])': {
      userSelect: 'text',
      WebkitUserSelect: 'text',
      WebkitTouchCallout: 'default',
    },
    // Touch/mouse focus must not leave a sticky browser outline (esp. mobile).
    // Keyboard users still get rings via :focus-visible on controls that define them.
    ':focus:not(:focus-visible)': {
      outline: 'none',
    },
  },
});

export const system = createSystem(defaultConfig, config);
